import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { Card, HeroPill, MobileHero, MobilePage, SectionHeader, Skeleton, StatCard } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const formatCurrency = (value) => `₵${Number(value || 0).toFixed(2)}`;

const MobileEarnings = () => {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEarnings = async () => {
      try {
        const response = await axios.get(`${API_URL}/practitioner/earnings`);
        setEarnings(response.data.earnings || { total: 0, pending: 0, withdrawn: 0, available: 0 });
      } catch (error) {
        console.error('Mobile earnings load error:', error);
        setEarnings({ total: 0, pending: 0, withdrawn: 0, available: 0 });
      } finally {
        setLoading(false);
      }
    };
    loadEarnings();
  }, []);

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Revenue Tracking</HeroPill>
        <h2>My Earnings</h2>
        <p>Track assessment revenue, pending payouts, and paid earnings from your phone.</p>
      </MobileHero>

      {loading ? <Skeleton $height="320px" /> : (
        <>
          <InfoCard>
            <strong>20% practitioner share</strong>
            <span>You receive a share of each paid assessment. Payouts are processed by the admin team.</span>
          </InfoCard>

          <StatsGrid>
            <PrimaryStat $accent="#2563eb"><span>Total earned</span><strong>{formatCurrency(earnings?.total)}</strong><small>Lifetime earnings</small></PrimaryStat>
            <StatCard $accent="#d97706"><span>Pending payout</span><strong>{formatCurrency(earnings?.pending)}</strong><small>Awaiting processing</small></StatCard>
            <StatCard $accent="#059669"><span>Already paid</span><strong>{formatCurrency(earnings?.withdrawn || earnings?.paid)}</strong><small>Processed payouts</small></StatCard>
            <StatCard $accent="#7c3aed"><span>Available</span><strong>{formatCurrency(earnings?.available)}</strong><small>Ready balance</small></StatCard>
          </StatsGrid>

          <Card>
            <SectionHeader><div><span>How it works</span><h3>Payout flow</h3></div></SectionHeader>
            <Steps>
              <li><strong>Client pays</strong><span>Your share is calculated when an assessment is paid.</span></li>
              <li><strong>Pending payout</strong><span>Earnings wait in pending until admin processing.</span></li>
              <li><strong>Admin processes payout</strong><span>The admin team reviews and processes payouts on a periodic basis.</span></li>
              <li><strong>Payment received</strong><span>Processed payouts move to already paid and you receive your funds.</span></li>
              <li><strong>Need help?</strong><span>Contact the admin team for payout or payment-related questions.</span></li>
            </Steps>
          </Card>

          <Card>
            <SectionHeader><div><span>Payment method</span><h3>Mobile Money</h3></div></SectionHeader>
            <PaymentMethod><div className="icon">📱</div><div><strong>Mobile Money</strong><p>Payouts are typically made via Mobile Money. Ensure your phone number in your profile is correct for receiving payments.</p><ProviderRow><span>📱 MTN MoMo</span><span>📱 Vodafone Cash</span><span>📱 AirtelTigo Money</span></ProviderRow></div></PaymentMethod>
          </Card>
        </>
      )}
    </MobilePage>
  );
};

const StatsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const PrimaryStat = styled(StatCard)`
  background: linear-gradient(135deg, #0c2d48, #0284c7);
  grid-column: 1 / -1;
  strong, span, small { color: white; }
  small { opacity: 0.78; }
`;

const InfoCard = styled.section`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 22px;
  padding: 15px;
  strong { color: #075985; display: block; font-size: 0.92rem; }
  span { color: #0369a1; display: block; font-size: 0.8rem; line-height: 1.45; margin-top: 5px; }
`;

const PaymentMethod = styled.div`
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  display: flex;
  gap: 12px;
  padding: 14px;
  .icon { align-items: center; background: #e0f2fe; border-radius: 16px; display: flex; height: 44px; justify-content: center; width: 44px; }
  strong { color: #0f172a; display: block; }
  p { color: #64748b; font-size: 0.8rem; line-height: 1.45; margin: 4px 0 0; }
`;

const ProviderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
  span { background: white; border: 1px solid #e2e8f0; border-radius: 999px; color: #475569; font-size: 0.7rem; font-weight: 850; padding: 7px 9px; }
`;

const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    padding: 12px;
  }

  strong { color: #0f172a; display: block; font-size: 0.88rem; }
  span { color: #64748b; display: block; font-size: 0.76rem; line-height: 1.45; margin-top: 4px; }
`;

export default MobileEarnings;
